import { useRecords, quickUpdate } from '../lib/api'
import { useSession } from '../lib/auth'
import { Link } from '../lib/router'
import { fmtDay, fmtDayLong, fmtTime, today, usd } from '../lib/format'
import { Avatar, Badge, Icon, Skeleton, Star, useToast } from '../components/ui'
import { pillarColor } from './Marketing'
import { academiaProgress } from './Academia'

export function Dashboard() {
  const { name } = useSession()
  const toast = useToast()
  const props = useRecords('propiedades')
  const clientes = useRecords('clientes')
  const visitas = useRecords('visitas')
  const tareas = useRecords('tareas')
  const marketing = useRecords('marketing')
  const academia = useRecords('academia')

  const t = today()
  const now = Date.now()
  const in7 = now + 7 * 86400000
  const disponibles = props.data.filter(p => p.estado === 'Disponible')
  const seguimiento = clientes.data.filter(c => !['Cerrado', 'Perdido'].includes(c.estado))
  const proximas = visitas.data.filter(v => v.inicio && new Date(v.inicio).getTime() > now - 3600000 && v.estado !== 'Cancelada' && v.estado !== 'Realizada')
    .sort((a, b) => a.inicio.localeCompare(b.inicio))
  const pendientes = tareas.data.filter(x => x.estado !== 'Hecha').sort((a, b) => (a.vence || '9').localeCompare(b.vence || '9'))
  const programadas = marketing.data.filter(m => m.tipo === 'Publicación' && m.fecha >= t && m.estado !== 'Publicado').sort((a, b) => a.fecha.localeCompare(b.fecha))
  const docsPend = clientes.data.filter(c => c.cumplimiento !== 'Aprobado' && !['Perdido'].includes(c.estado))
  const prog = academiaProgress(academia.data)
  const loading = props.loading || clientes.loading

  const hour = new Date().getHours()
  const saludo = hour < 12 ? 'Buen día' : hour < 20 ? 'Buenas tardes' : 'Buenas noches'
  const lookup = (id: string, list: any[]) => list.find(x => x.id === id)

  return (
    <div className="page dashboard">
      <header className="dash-hero">
        <div>
          <div className="eyebrow">{fmtDayLong(t)}</div>
          <h1>{saludo}, {name}.</h1>
          <p className="page-sub">Esto es lo que se mueve hoy en REVELA.</p>
        </div>
        <div className="quick">
          <Link to="/propiedades?nuevo=1" className="quick-btn"><Icon name="building" /><span>Nueva propiedad</span></Link>
          <Link to="/clientes?nuevo=1" className="quick-btn"><Icon name="users" /><span>Registrar cliente</span></Link>
          <Link to="/agenda?nuevo=1" className="quick-btn"><Icon name="calendar" /><span>Programar visita</span></Link>
          <Link to="/marketing?nuevo=1" className="quick-btn quick-accent"><Star size={15} color="currentColor" /><span>Idea de contenido</span></Link>
        </div>
      </header>

      {loading ? <Skeleton rows={2} /> : (
        <section className="stats">
          <Stat to="/propiedades" label="Propiedades disponibles" value={disponibles.length} note={`${props.data.filter(p => p.estado === 'Captación').length} en captación`} />
          <Stat to="/clientes" label="Clientes en seguimiento" value={seguimiento.length} note={`${seguimiento.filter(c => c.segmento === 'Inversor del exterior').length} inversores del exterior`} />
          <Stat to="/agenda" label="Visitas próximos 7 días" value={proximas.filter(v => new Date(v.inicio).getTime() < in7).length} note={proximas[0] ? `Próxima: ${fmtDay(proximas[0].inicio.slice(0, 10))} ${fmtTime(proximas[0].inicio)}` : 'Sin visitas'} />
          <Stat to="/tareas" label="Tareas pendientes" value={pendientes.length} note={`${pendientes.filter(x => x.vence && x.vence < t).length} vencidas`} alert={pendientes.some(x => x.vence && x.vence < t)} />
          <Stat to="/marketing" label="Publicaciones programadas" value={programadas.length} note={`${programadas.filter(p => p.estado === 'Grabado').length} ya grabadas`} />
          <Stat to="/clientes?cumplimiento=pendiente" label="Documentación pendiente" value={docsPend.length} note="Clientes sin revisión aprobada" alert={docsPend.length > 0} />
          <Stat to="/academia" label="Academia REVELA" value={`${Math.round(prog.pct * 100)} %`} note={`${prog.listos} de ${prog.total} piezas listas`} bar={prog.pct} />
        </section>
      )}

      <div className="dash-grid">
        <section className="panel">
          <header className="panel-head"><h2>Próximas visitas</h2><Link to="/agenda" className="panel-link">Ver agenda</Link></header>
          {visitas.loading ? <Skeleton /> : proximas.length ? (
            <ul className="agenda-list">
              {proximas.slice(0, 5).map(v => {
                const p = lookup(v.propiedad, props.data), c = lookup(v.cliente, clientes.data)
                return (
                  <li key={v.id}>
                    <Link to={`/agenda?abrir=${v.id}`} className="agenda-row">
                      <div className="agenda-when"><strong>{fmtTime(v.inicio)}</strong><span>{fmtDay(v.inicio.slice(0, 10))}</span></div>
                      <div className="agenda-what"><strong>{v.titulo}</strong><span>{[c?.nombre, p?.direccion].filter(Boolean).join(' · ') || v.lugar}</span></div>
                      <Avatar name={v.socia} /><Badge>{v.estado}</Badge>
                    </Link>
                  </li>
                )
              })}
            </ul>
          ) : <p className="muted pad">No hay visitas programadas.</p>}
        </section>

        <section className="panel">
          <header className="panel-head"><h2>Tareas pendientes</h2><Link to="/tareas" className="panel-link">Ver todas</Link></header>
          {tareas.loading ? <Skeleton /> : pendientes.length ? (
            <ul className="task-list">
              {pendientes.slice(0, 6).map(x => (
                <li key={x.id} className={x.vence && x.vence < t ? 'overdue' : ''}>
                  <button type="button" className="check" aria-label={`Marcar "${x.titulo}" como hecha`} onClick={async () => {
                    try { await quickUpdate('tareas', x, { estado: 'Hecha' }); toast('Tarea completada') } catch (e: any) { toast(e.message, 'error') }
                  }}><Icon name="check" size={13} /></button>
                  <Link to={`/tareas?abrir=${x.id}`} className="task-title">{x.titulo}</Link>
                  <span className="task-meta">{x.area}{x.vence && ` · ${fmtDay(x.vence)}`}</span>
                  <Avatar name={x.socia} />
                </li>
              ))}
            </ul>
          ) : <p className="muted pad">Todo al día.</p>}
        </section>

        <section className="panel">
          <header className="panel-head"><h2>Próximas publicaciones</h2><Link to="/marketing" className="panel-link">Calendario editorial</Link></header>
          {marketing.loading ? <Skeleton /> : programadas.length ? (
            <ul className="post-list">
              {programadas.slice(0, 5).map(m => (
                <li key={m.id}>
                  <Link to={`/marketing?abrir=${m.id}`} className="post-row">
                    <span className="pillar-dot" style={{ background: pillarColor(m.pilar) }} />
                    <div><strong>{m.titulo}</strong><span>{fmtDay(m.fecha)} · {m.formato} · {m.pilar}</span></div>
                    <Badge>{m.estado}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          ) : <p className="muted pad">No hay publicaciones programadas.</p>}
        </section>

        <section className="panel">
          <header className="panel-head"><h2>Propiedades destacadas</h2><Link to="/propiedades" className="panel-link">Inventario</Link></header>
          {props.loading ? <Skeleton /> : (
            <ul className="mini-props">
              {disponibles.slice(0, 4).map(p => (
                <li key={p.id}>
                  <Link to={`/propiedades?abrir=${p.id}`} className="mini-prop">
                    <div className="mini-img">{p.foto ? <img src={p.foto} alt="" /> : <Star size={16} color="var(--arena-dark)" />}</div>
                    <div><strong>{p.nombre}</strong><span>{p.ciudad} · {usd(p.precio)}</span></div>
                    <Avatar name={p.socia} />
                  </Link>
                </li>
              ))}
              {!disponibles.length && <p className="muted pad">Todavía no hay propiedades disponibles.</p>}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

function Stat({ to, label, value, note, alert, bar }: { to: string; label: string; value: number | string; note?: string; alert?: boolean; bar?: number }) {
  return (
    <Link to={to} className={`stat ${alert ? 'stat-alert' : ''}`}>
      <span className="stat-label">{label}</span>
      <strong className="stat-value">{value}</strong>
      {bar != null && <span className="stat-bar"><span style={{ width: `${Math.round(bar * 100)}%` }} /></span>}
      {note && <span className="stat-note">{alert && <span className="alert-dot" />}{note}</span>}
    </Link>
  )
}
